import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { safeUrl } from '../utils/safeUrl';
import { isAdminLike } from '../utils/roles';
import { useQueryClient } from '@tanstack/react-query';
import { useEventPricing, eventPricingKeys } from '../hooks/useEventPricing';
import { useEventSales, eventSalesKeys } from '../hooks/useEventSales';
import { useVenueData } from '../hooks/useVenueData';
import { updatePrice, bulkUpdatePrices, getEventPricing, getEventSales, getSkyboxHistory } from '../api';
import { describePriceError, priceLimitsHint } from '../utils/priceErrors';
import { inventoryKeys } from '../hooks/useInventory';
import { useLPricePoll } from '../hooks/useLPricePoll';
import { useTagColors } from '../hooks/useTagColors';
import { useEventNotes, useUpdateEventNotes } from '../hooks/useNotes';
import { useEventAi, useAnalyzeEvent, useAskEvent, useAcceptSuggestion, useDismissSuggestion, useRateSuggestion, progressLabel } from '../hooks/useAi';
import AiEventPanel, { FLAG_LABELS, FLAG_HELP } from '../components/AiEventPanel';
import { useTicketMetric } from '../hooks/useTicketMetric';
import TicketMetricPanel from '../components/TicketMetricPanel';
import AiBulkReviewModal from '../components/AiBulkReviewModal';
import AiRatePopover from '../components/AiRatePopover';
import DismissReasonBar from '../components/DismissReasonBar';
import BulkPriceModal from '../components/BulkPriceModal';
import MergeListingsModal from '../components/MergeListingsModal';
import { useMergePlan, mergePlanKeys } from '../hooks/useListingMerge';
import { mergeButtonState } from '../utils/mergeRun';
import RetryCountdown from '../components/RetryCountdown';
import CompareSeatMap from '../components/CompareSeatMap';
import { siblingPageUrl } from '../utils/compareLink';
import { formatDateTimeEastern, formatDateEastern, formatTimeEastern, formatEventDateTime, timeAgo, parseUtcDate } from '../utils/dates';
import { REASON_LABELS, feedbackLine, ladderLine } from '../utils/aiFeedback';

// One-line verdict for the admin "Skybox history" lookup: does Vivid's L
// match Skybox's own what-if for the listing's current B?
const skyboxHistoryVerdict = (h) => {
  if (!(h.listPrice > 0)) return 'No B price - nothing to publish.';
  if (h.unitPrice == null || !(h.unitPrice > 0)) return '⚠ Vivid shows no price for this listing (not published yet, or not listed).';
  if (h.whatIf == null) return 'What-if unavailable - compare Vivid L against the price trail by hand.';
  return Math.abs(h.unitPrice - h.whatIf) < 1
    ? '✓ Vivid matches Skybox\'s what-if for the current B - this price is live.'
    : `⚠ Vivid's L ($${h.unitPrice}) is not the what-if for B $${h.listPrice} ($${h.whatIf}) - Skybox has not pushed this price to Vivid.`;
};

// Skybox group colors arrive in mixed formats (int, "0x…", "rrggbb", "#rrggbb")
const normalizeGroupColor = (rawColor) => {
  if (rawColor == null) return null;
  if (typeof rawColor === 'number') return '#' + rawColor.toString(16).padStart(6, '0');
  if (typeof rawColor === 'string' && rawColor) {
    if (rawColor.startsWith('0x')) return '#' + rawColor.slice(2);
    if (!rawColor.startsWith('#')) return '#' + rawColor;
    return rawColor;
  }
  return null;
};

// "#rrggbb" → rgba() at the given alpha (tints my-row backgrounds per group)
const hexToRgba = (hex, alpha) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// Sticky row-error entry for a rejected write. A 429 carries the wall-clock
// instant a retry is worth attempting; attemptedPrice lets a caller tell a
// fresh edit from a re-send of the same rejected number.
const rowErrorEntry = (err, attemptedPrice) => {
  const info = describePriceError(err);
  return {
    ...info,
    attemptedPrice,
    retryAt: info.status === 429 ? Date.now() + (info.retryAfterSec || 30) * 1000 : null,
  };
};

// "$140" / "$140.50" — compact money for chips and the dismiss bar
const shortMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
};

// Admin chip for a listing whose latest suggestion is no longer live:
// "accepted $140", "modified $132", "dismissed $120"
const latestStatusText = (s) => {
  const applied = s.appliedPrice != null && (s.status === 'accepted' || s.status === 'modified');
  const price = applied ? shortMoney(s.appliedPrice) : (s.action === 'hold' ? 'hold' : shortMoney(s.suggestedPrice));
  return `${s.status || 'suggested'} ${price}`;
};

function Compare({ user }) {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Track confirmed price updates (survives refreshes)
  const confirmedPriceUpdates = useRef({});

  const queryClient = useQueryClient();

  const [error, setError] = useState('');
  const [selectedSections, setSelectedSections] = useState([]);
  const [quantityFilter, setQuantityFilter] = useState('any');
  const [showMyListingsFirst, setShowMyListingsFirst] = useState(true);
  const [editingPrices, setEditingPrices] = useState({});
  const [savingPrices, setSavingPrices] = useState({});
  // Rejected writes, keyed String(inventoryId) so they survive the row remount
  // that a price change forces (row keys include listPrice). A rejection must
  // stay next to the typed number — the old toast-only path let pricers walk
  // away believing a price landed when the backend had refused it.
  const [rowErrors, setRowErrors] = useState({});
  // Rows whose bulk apply timed out: id -> { intendedPrice, priorPrice }.
  // Locked until a fresh read shows what the server actually did.
  const [pendingOutcome, setPendingOutcome] = useState({});
  const [applyingAll, setApplyingAll] = useState(false);
  const clearRowError = useCallback((inventoryId) => {
    setRowErrors(prev => {
      const key = String(inventoryId);
      if (prev[key] === undefined) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);
  const putRowError = useCallback((inventoryId, entry) => {
    setRowErrors(prev => ({ ...prev, [String(inventoryId)]: entry }));
  }, []);
  const [successMessage, setSuccessMessage] = useState('');
  const [priceUpdateKey, setPriceUpdateKey] = useState(0); // Force re-render on price update
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });

  // AI assistant: suggestions/flags/summary for this event. `ai` is null
  // whenever the assistant is disabled or unavailable, and every AI element
  // below gates on it — the page renders exactly as before in that case.
  const { data: aiData } = useEventAi(eventId);
  const ai = aiData?.enabled ? aiData : null;
  const analyzeMutation = useAnalyzeEvent(eventId);
  const askMutation = useAskEvent(eventId);
  const acceptMutation = useAcceptSuggestion(eventId);
  const dismissMutation = useDismissSuggestion(eventId);
  const rateMutation = useRateSuggestion(eventId);
  const isAdmin = isAdminLike(user?.role);
  const [expandedSuggestionId, setExpandedSuggestionId] = useState(null);
  // Pricers: open "why dismiss?" bars, keyed String(inventoryId) → suggestion.
  // Nothing is dismissed until a reason is picked or skipped.
  const [dismissing, setDismissing] = useState({});
  // Admins: the one open rate popover { sug, listing, anchorEl, mode: 'rate'|'dismiss' }
  const [ratePopover, setRatePopover] = useState(null);
  const [showBulkReview, setShowBulkReview] = useState(false);

  // Bulk price update: my rows ticked in the listings table (ids as strings)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkPrice, setShowBulkPrice] = useState(false);
  // Merge listings: the plan feeds the button's count; the modal does the rest
  const mergePlan = useMergePlan(eventId);
  const [showMerge, setShowMerge] = useState(false);
  const mergeButtonRef = useRef(null); // focus returns here when the modal closes
  const lastSelectClickRef = useRef(null); // anchor row for shift-click range selection
  useEffect(() => {
    setSelectedIds(new Set()); setShowBulkPrice(false); lastSelectClickRef.current = null;
    setDismissing({}); setRatePopover(null);
    setShowMerge(false);
  }, [eventId]);

  // Pending (or stale) suggestions keyed by String(inventoryId) — all
  // inventory-id comparisons in this file go through String()
  const suggestionsByInvId = useMemo(() => {
    const m = new Map();
    for (const s of ai?.suggestions || []) {
      if (s.status === 'pending' || s.status === 'stale') m.set(String(s.inventoryId), s);
    }
    return m;
  }, [ai]);
  // Newest suggestion per listing in the last 30 days, any status (admins rate
  // accepted / dismissed ones from here). Absent on older servers → {}.
  const latestByInventory = useMemo(
    () => (ai?.latestByInventory && typeof ai.latestByInventory === 'object' ? ai.latestByInventory : {}),
    [ai]
  );
  // A "why dismiss?" bar whose suggestion was replaced (re-analysis) or acted
  // on elsewhere has nothing left to dismiss — drop it
  useEffect(() => {
    setDismissing(prev => {
      const keys = Object.keys(prev);
      const keep = keys.filter(k => suggestionsByInvId.get(k)?.id === prev[k].id);
      if (keep.length === keys.length) return prev;
      return Object.fromEntries(keep.map(k => [k, prev[k]]));
    });
  }, [suggestionsByInvId]);
  const flagsByInvId = useMemo(() => {
    const m = new Map();
    for (const f of ai?.flags || []) {
      if (!f.inventoryId) continue;
      const k = String(f.inventoryId);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(f);
    }
    return m;
  }, [ai]);
  const pendingSuggestionCount = useMemo(
    () => [...suggestionsByInvId.values()].filter(s => s.status === 'pending').length,
    [suggestionsByInvId]
  );
  // Admins also get the column for listings whose latest suggestion was
  // already acted on, so they can still rate it
  const showAiColumn = !!ai && (suggestionsByInvId.size > 0 || (isAdmin && Object.keys(latestByInventory).length > 0));

  // Per-listing price history summaries (repriced ×N, trajectory) keyed by inventoryId
  const historyByInvId = ai?.priceHistory || {};
  const historyLine = (ph) => {
    if (!ph) return '';
    const path = (ph.levels || []).map(l => `$${Number(l.price).toFixed(0)}`).join(' → ');
    const dwell = ph.daysAtCurrentPrice >= 1 ? `${ph.daysAtCurrentPrice}d at current` : `${ph.hoursAtCurrentPrice ?? 0}h at current`;
    return [path, `${ph.repriceCount} reprice${ph.repriceCount === 1 ? '' : 's'}`, dwell, ph.lastChangedBy ? `last by ${ph.lastChangedBy}` : null]
      .filter(Boolean).join(' · ');
  };

  // API Debug Panel state
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [apiLogs, setApiLogs] = useState([]);
  const [skyboxLookupId, setSkyboxLookupId] = useState('');
  const [skyboxLookupBusy, setSkyboxLookupBusy] = useState(false);

  // Get event info from URL params
  const eventName = searchParams.get('name') || 'Event';
  const eventDate = searchParams.get('date');
  const venue = searchParams.get('venue');
  const venueCity = searchParams.get('city');
  const venueState = searchParams.get('state');
  // Scheme-checked: this comes straight from the query string, and a
  // javascript: href would execute in-origin on click
  const vividSeatsUrl = safeUrl(searchParams.get('vividUrl'));

  // Event dates are venue-local wall clock — display verbatim, no tz conversion
  const formatEventDate = (dateStr) => formatEventDateTime(dateStr);

  // Pricing data hook
  const {
    data: rawPricingData,
    isLoading: loading,
    error: pricingError,
    refetch: refetchPricing,
  } = useEventPricing(eventId);

  const pricingData = useMemo(() => {
    if (!rawPricingData) return null;
    return { ...rawPricingData, eventName, vividSeatsUrl };
  }, [rawPricingData, eventName, vividSeatsUrl]);

  // TicketMetric sold panel. Called here, above the loading early return, so
  // its one POST goes out at once and the numbers can show while Skybox
  // pricing is still loading. Our section names are deduped, sorted and
  // keyed by content: price writes patch myInventory constantly, and the
  // array must only change when the set of names does (it is part of the
  // query key). Independent of the Refresh button on purpose — that one
  // fires after every price write.
  const tmSectionsKey = useMemo(() => {
    const names = new Set();
    for (const l of rawPricingData?.myInventory || []) {
      const n = String(l.displayName || l.section || '').trim();
      if (n) names.add(n);
    }
    return JSON.stringify([...names].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })));
  }, [rawPricingData?.myInventory]);
  const tmSections = useMemo(() => JSON.parse(tmSectionsKey), [tmSectionsKey]);
  const tm = useTicketMetric(eventId, { sections: tmSections });

  // L-price convergence after writes: a just-bought listing has no L until
  // Skybox publishes its Vivid listing (seconds to ~2 min). The hook polls a
  // slim endpoint on a bounded backoff and patches only the L fields into the
  // pricing cache; pins keep Skybox's what-if L visible meanwhile. Each tick
  // is logged to the API Debug Panel.
  const lPoll = useLPricePoll(eventId, {
    onTick: (t) => addApiLog(t.error ? 'ERROR' : 'RESPONSE', {
      method: 'GET',
      endpoint: `/api/inventory/events/${t.eventId}/lprices (L check #${t.attempt})`,
      status: t.error ? 'poll failed' : 200,
      duration: `${t.durationMs}ms`,
      ...(t.error
        ? { error: t.error }
        : {
          response: {
            resolved: t.resolved.map(r => `${r.id} → $${r.unitPrice} after ${Math.round(r.afterMs / 1000)}s${r.origin === 'verify' ? ' (Vivid took the new price)' : ''}`),
            stillPending: t.pending,
            notOnVivid: t.stale,
          },
        }),
    }),
  });
  const { rearmFrom: lPollRearm } = lPoll;
  // Re-arm rule: whenever a full payload lands (mount, Refresh, F5, another
  // pricer's write, bulk, Dashboard edit) track own rows that are priced but
  // still have no L, so every view converges without touching write paths.
  useEffect(() => {
    if (rawPricingData?.myInventory) lPollRearm(rawPricingData.myInventory);
  }, [rawPricingData, lPollRearm]);

  // Set error from pricing query
  const pricingErrorStatus = pricingError?.response?.status;
  const pricingErrorRetryable =
    !!pricingError &&
    pricingErrorStatus !== 404 &&
    (pricingErrorStatus === 503 ||
      pricingError.code === 'ECONNABORTED' ||
      pricingError.message?.includes('timeout') ||
      !pricingErrorStatus); // network error (no response)
  const pricingQueryError = pricingError
    ? (pricingErrorStatus === 404
      ? 'Pricing data not found for this event'
      : pricingErrorRetryable
        ? 'Skybox is temporarily unavailable — this usually clears in a moment.'
        : 'Failed to load pricing data')
    : '';

  // Venue map data hook
  const {
    data: venueData,
    isLoading: mapLoading,
  } = useVenueData(eventId);

  // Sales data hook
  const {
    data: salesData,
    isLoading: salesLoading,
  } = useEventSales(eventId);

  const { data: tagColors = {} } = useTagColors();

  // Performer tier + tour notes + individual event notes (SQLite-backed,
  // independent of the Skybox pricing fetch)
  const { data: notesData } = useEventNotes(eventId);
  const saveNotesMutation = useUpdateEventNotes(eventId);
  const [eventNotesDraft, setEventNotesDraft] = useState('');
  const lastServerNotes = useRef('');

  // Adopt server value only while the draft is clean, so a background refetch
  // (or the Refresh button) never clobbers an in-progress edit. The old server
  // value must be captured BEFORE mutating the ref: the functional updater
  // runs on the next render, after the ref has already been reassigned.
  const serverEventNotes = notesData?.eventNote?.notes || '';
  useEffect(() => {
    const prevServer = lastServerNotes.current;
    if (serverEventNotes !== prevServer) {
      setEventNotesDraft(prev => (prev === prevServer ? serverEventNotes : prev));
      lastServerNotes.current = serverEventNotes;
    }
  }, [serverEventNotes]);

  const handleSaveNotes = () => {
    saveNotesMutation.mutate(eventNotesDraft, {
      onSuccess: (_data, savedNotes) => {
        const trimmed = savedNotes.trim();
        lastServerNotes.current = trimmed;
        // Only normalize when the draft is unchanged since the click, so
        // keystrokes typed during an in-flight save are never lost
        setEventNotesDraft(prev => (prev === savedNotes ? trimmed : prev));
        setSuccessMessage('Notes saved');
        setTimeout(() => setSuccessMessage(''), 2000);
      },
      onError: (err) => {
        setError(err.response?.data?.error || 'Failed to save notes');
        setTimeout(() => setError(''), 5000);
      },
    });
  };

  const [showSales, setShowSales] = useState(false);

  const svgContent = venueData?.svg || null;
  const chartData = useMemo(() => {
    if (!venueData?.elements) return null;
    return {
      width: venueData.chartWidth,
      height: venueData.chartHeight,
      elements: venueData.elements,
      sections: venueData.sections,
      groups: venueData.groups,
      event: venueData.event,
    };
  }, [venueData]);

  // Map section click: add or remove it from the table filter. Stable
  // identity so the memoized seat map doesn't re-render on page renders.
  const toggleSection = useCallback(sectionData => setSelectedSections(prev => (
    prev.some(s => s.id === sectionData.id)
      ? prev.filter(s => s.id !== sectionData.id)
      : [...prev, sectionData]
  )), []);

  // Chart section ids someone is selling in (ours + competitors), so the map
  // can grey out the rest. From the live pricing payload, which refreshes
  // with the table, not the 30-min venue cache. Listings without a sectionId
  // match by exact display name, the same rule as the table's section filter.
  // null until pricing loads, so nothing greys out early.
  const availableSectionIds = useMemo(() => {
    if (!pricingData) return null;
    const ids = new Set();
    let idsByName = null;
    for (const l of [...(pricingData.myInventory || []), ...(pricingData.competitorListings || [])]) {
      if (l.sectionId) { ids.add(String(l.sectionId)); continue; }
      const name = (l.displayName || l.section || '').toLowerCase().trim();
      if (!name || !chartData?.sections) continue;
      if (!idsByName) {
        idsByName = new Map();
        for (const [id, sec] of Object.entries(chartData.sections)) {
          const n = (sec?.name || '').toLowerCase().trim();
          if (!n) continue;
          if (!idsByName.has(n)) idsByName.set(n, []);
          idsByName.get(n).push(id);
        }
      }
      for (const id of idsByName.get(name) || []) ids.add(id);
    }
    return ids;
  }, [pricingData, chartData]);

  // Get unique groups from chart data for the group selector
  const getChartGroups = useCallback(() => {
    if (!chartData || !chartData.groups || !chartData.sections || !chartData.elements) return [];

    const { elements, sections, groups } = chartData;
    const groupMap = new Map();

    // Get colors from pricingData.sectionGroups (from the API)
    const sectionGroups = pricingData?.sectionGroups || [];

    // Collect all sections for each group
    elements.forEach(el => {
      if (el.type !== 'path' || !el.id) return;
      const sectionInfo = sections[el.id];
      if (!sectionInfo) return;

      const groupId = sectionInfo.groupId;
      const groupName = groups[groupId];
      if (!groupName) return;

      if (!groupMap.has(groupId)) {
        // Find matching group in pricingData.sectionGroups by name
        const apiGroup = sectionGroups.find(g =>
          g.name?.toLowerCase() === groupName?.toLowerCase()
        );

        // Get color from API group data
        const color = normalizeGroupColor(apiGroup?.color) || '#888888';

        groupMap.set(groupId, {
          id: groupId,
          name: groupName,
          color: color,
          sections: []
        });
      }

      groupMap.get(groupId).sections.push({
        id: el.id,
        name: sectionInfo.name,
        group: groupName,
        lowestPrice: sectionInfo.lowestPrice,
        highestPrice: sectionInfo.highestPrice
      });
    });

    return Array.from(groupMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [chartData, pricingData]);

  // Section-group color for each listing row (same palette as the map and the
  // group panel). byGroupId covers my inventory (groupId from Skybox); the
  // sectionId fallback covers competitor listings without a groupId.
  const listingGroupColors = useMemo(() => {
    const byGroupId = new Map();
    const byName = new Map();
    for (const g of (pricingData?.sectionGroups || [])) {
      const color = normalizeGroupColor(g.color);
      if (!color) continue;
      if (g.id != null) byGroupId.set(String(g.id), color);
      if (g.name) byName.set(g.name.toLowerCase(), color);
    }
    const bySectionId = new Map();
    if (chartData?.sections && chartData?.groups) {
      for (const [sectionId, info] of Object.entries(chartData.sections)) {
        const groupName = chartData.groups[info.groupId];
        const color = groupName ? byName.get(groupName.toLowerCase()) : null;
        if (color) bySectionId.set(String(sectionId), color);
      }
    }
    return { byGroupId, bySectionId };
  }, [pricingData, chartData]);

  const getListingColor = (listing) => {
    if (listing.groupId != null) {
      const c = listingGroupColors.byGroupId.get(String(listing.groupId));
      if (c) return c;
    }
    if (listing.sectionId) {
      const c = listingGroupColors.bySectionId.get(String(listing.sectionId));
      if (c) return c;
    }
    return null;
  };

  // Check if a group is fully selected
  const isGroupSelected = (group) => {
    return group.sections.every(section =>
      selectedSections.some(s => s.id === section.id)
    );
  };

  // Check if a group is partially selected
  const isGroupPartiallySelected = (group) => {
    const selectedCount = group.sections.filter(section =>
      selectedSections.some(s => s.id === section.id)
    ).length;
    return selectedCount > 0 && selectedCount < group.sections.length;
  };

  // Toggle all sections in a group
  const toggleGroup = (group) => {
    if (isGroupSelected(group)) {
      // Deselect all sections in this group
      setSelectedSections(prev =>
        prev.filter(s => !group.sections.some(gs => gs.id === s.id))
      );
    } else {
      // Select all sections in this group
      setSelectedSections(prev => {
        const newSections = [...prev];
        group.sections.forEach(section => {
          if (!newSections.some(s => s.id === section.id)) {
            newSections.push(section);
          }
        });
        return newSections;
      });
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return '$' + parseFloat(value).toFixed(2);
  };

  // Add API log entry (admin-only feature; skip accumulating logs for other roles)
  const addApiLog = (type, details) => {
    if (!isAdminLike(user?.role)) return;
    const entry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      type,
      ...details
    };
    setApiLogs(prev => [entry, ...prev].slice(0, 50)); // Keep last 50 logs
  };

  // Admin forensics: Skybox B vs Vivid L vs what-if plus the POS price trail
  // for one inventory id, logged as a Debug-panel entry
  const handleSkyboxLookup = async () => {
    const id = String(skyboxLookupId || '').trim();
    if (!/^\d+$/.test(id) || skyboxLookupBusy) return;
    const endpoint = `/api/inventory/${id}/skybox-history`;
    setSkyboxLookupBusy(true);
    const started = Date.now();
    try {
      const res = await getSkyboxHistory(id);
      addApiLog('RESPONSE', { method: 'GET', endpoint, status: 200, duration: `${Date.now() - started}ms`, skyboxHistory: res.data });
    } catch (err) {
      addApiLog('ERROR', { method: 'GET', endpoint, status: err.response?.status || 'Network Error', error: err.response?.data?.error || err.message });
    } finally {
      setSkyboxLookupBusy(false);
    }
  };

  const handlePriceChange = (inventoryId, value) => {
    // Typing a new number retires the old rejection: whatever the server
    // refused, this is a different attempt.
    clearRowError(inventoryId);
    setEditingPrices(prev => ({
      ...prev,
      [inventoryId]: value
    }));
  };

  // Helper function to apply confirmed price updates to inventory data
  const applyConfirmedPriceUpdates = (inventory) => {
    if (!inventory) return inventory;
    return inventory.map(item => {
      const confirmedPrice = confirmedPriceUpdates.current[String(item.id)];
      if (confirmedPrice !== undefined) {
        return { ...item, listPrice: confirmedPrice };
      }
      return item;
    });
  };

  // Typed-but-unsaved B prices, for the "Apply N changes" bar. Derived over
  // myInventory (not the section/qty-filtered `mine`) so changing a filter can
  // never silently drop an edit from the batch, and over the confirmed-price
  // overlay so a row we just wrote isn't counted as still edited.
  const pendingEdits = applyConfirmedPriceUpdates(pricingData?.myInventory || []).reduce((acc, l) => {
    const raw = editingPrices[l.id];
    if (raw === undefined || raw === '') return acc;
    if (pendingOutcome[String(l.id)]) return acc; // timed-out row: outcome unknown, never re-send
    const newPrice = Math.round(Number(raw));
    if (!Number.isFinite(newPrice) || newPrice < 0) return acc;
    if (Math.abs(newPrice - (Number(l.listPrice) || 0)) < 0.5) return acc;
    acc.push({ id: String(l.id), listing: l, newPrice });
    return acc;
  }, []);

  // Post-write cache bookkeeping shared by the manual SET path and the AI
  // apply path: pin the confirmed price, patch the eventPricing cache in place
  // (never invalidate it — that triggers a real Skybox fetch server-side),
  // refresh the Dashboard inventory, clear editing state, toast.
  const applyPriceResult = (inventoryId, newPrice, newUnitPrice) => {
    const idKey = String(inventoryId);
    clearRowError(inventoryId); // this write landed; any earlier rejection is moot
    confirmedPriceUpdates.current[idKey] = newPrice;
    const prevItem = queryClient.getQueryData(eventPricingKeys.detail(eventId))?.myInventory
      ?.find(item => String(item.id) === idKey);
    const wasListed = !!(prevItem?.unitPrice > 0 && prevItem?.vividListingId);

    queryClient.setQueryData(
      eventPricingKeys.detail(eventId),
      (prev) => {
        if (!prev || !prev.myInventory) return prev;
        return {
          ...prev,
          myInventory: prev.myInventory.map(item => {
            if (String(item.id) === idKey) {
              return {
                ...item,
                listPrice: newPrice,
                ...(newUnitPrice != null ? { unitPrice: newUnitPrice } : {}),
                lastPriceUpdate: new Date().toISOString()
              };
            }
            const confirmedPrice = confirmedPriceUpdates.current[String(item.id)];
            if (confirmedPrice !== undefined) {
              return { ...item, listPrice: confirmedPrice };
            }
            return item;
          })
        };
      }
    );

    // Invalidate inventory cache so Dashboard refetches on navigate back
    queryClient.invalidateQueries({ queryKey: inventoryKeys.all });

    // L price: the backend already asked Skybox's what-if endpoint, so
    // newUnitPrice is Skybox's own number whenever it answered (it does even
    // for a just-bought listing with no Vivid listing yet). Pin it so a later
    // full refetch that still carries unitPrice 0 can't flip the cell to $0.
    // Rows that were not on Vivid before this write poll until Skybox
    // publishes the real value; rows that WERE on Vivid poll in verify mode -
    // the what-if is only Skybox's projection, and Vivid has been seen to lag
    // or drop rapid successive changes (2026-08-22), so the cell shows the
    // projection as unconfirmed until Vivid's real L moves, and flags the row
    // if it never does.
    if (newUnitPrice != null) lPoll.pin(inventoryId, newUnitPrice, newPrice);
    if (newPrice > 0) {
      const entry = { id: inventoryId, expectedListPrice: newPrice, vividListingId: prevItem?.vividListingId };
      if (wasListed) lPoll.track([{ ...entry, previousUnitPrice: prevItem.unitPrice, whatIf: newUnitPrice }], { origin: 'verify' });
      else lPoll.track([entry]);
    }

    setEditingPrices(prev => {
      const updated = { ...prev };
      delete updated[inventoryId];
      delete updated[idKey];
      return updated;
    });
    setPriceUpdateKey(prev => prev + 1);
    setSuccessMessage(`Price updated to $${newPrice}!`);
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  // One-click apply of an AI suggestion via the shared guarded path. `price`
  // (optional, the rate popover's "Save & apply $X") applies a different B —
  // the server records that as modified. Resolves { ok, oldPrice, newPrice }
  // or { ok: false, error } so the bulk modal can show the outcome in its row.
  const handleApplySuggestion = async (sug, listing, price) => {
    const inventoryId = listing.id;
    const newBPrice = price != null ? price : sug.suggestedPrice;
    setSavingPrices(prev => ({ ...prev, [inventoryId]: true }));
    addApiLog('REQUEST', {
      method: 'POST',
      endpoint: `/api/ai/suggestions/${sug.id}/accept`,
      payload: { price: newBPrice },
      inventoryDetails: { section: listing.section || listing.displayName, row: listing.row, quantity: listing.quantity, currentBPrice: listing.listPrice, newBPrice },
    });
    try {
      const res = await acceptMutation.mutateAsync({
        id: sug.id, ...(price != null ? { price } : {}), vividListingId: listing.vividListingId, zone: listing.zone,
      });
      addApiLog('RESPONSE', { method: 'POST', endpoint: `/api/ai/suggestions/${sug.id}/accept`, status: 200, response: res.data });
      applyPriceResult(inventoryId, res.data.newPrice, res.data.unitPrice);
      setExpandedSuggestionId(null);
      return { ok: true, oldPrice: res.data.oldPrice, newPrice: res.data.newPrice };
    } catch (err) {
      addApiLog('ERROR', { method: 'POST', endpoint: `/api/ai/suggestions/${sug.id}/accept`, status: err.response?.status || 'Network Error', error: err.response?.data?.error || err.message });
      putRowError(inventoryId, rowErrorEntry(err, newBPrice));
      const message = describePriceError(err).message;
      setError(message);
      setTimeout(() => setError(''), 6000);
      return { ok: false, error: message };
    } finally {
      setSavingPrices(prev => ({ ...prev, [inventoryId]: false }));
    }
  };

  // Bulk modal path: same accept call, listing looked up from the pricing cache
  const applySuggestionById = (sug, price) => {
    const listing = (queryClient.getQueryData(eventPricingKeys.detail(eventId))?.myInventory || [])
      .find(l => String(l.id) === String(sug.inventoryId));
    if (!listing) return Promise.resolve({ ok: false, error: 'Listing is no longer on this event' });
    return handleApplySuggestion(sug, listing, price);
  };

  const flash = (msg, ms = 2000) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), ms); };
  const flashError = (err, prefix) => {
    setError(`${prefix}: ${err?.response?.data?.error || err?.message || 'request failed'}`);
    setTimeout(() => setError(''), 6000);
  };
  // The dismiss route answers with the row's real status: a suggestion that
  // was accepted or superseded meanwhile is NOT dismissed — say so (same rule
  // as the bulk modal's dismissOutcome)
  const reportDismiss = (res, reasonCode) => {
    const st = res?.data?.status;
    if (st && st !== 'dismissed') {
      setError(`Not dismissed — already ${st}`);
      setTimeout(() => setError(''), 6000);
      return;
    }
    flash(reasonCode ? `Dismissed · ${REASON_LABELS[reasonCode] || reasonCode}` : 'Suggestion dismissed');
  };

  // × on a suggestion. Admins get the rate popover prefilled "wrong" (Save
  // dismisses and records their rating); pricers get the persistent reason
  // bar under the row. Either way nothing is dismissed until they choose.
  const handleDismissSuggestion = (sug, listing, anchorEl) => {
    if (isAdmin) {
      setRatePopover({ sug, listing, anchorEl: anchorEl || null, mode: 'dismiss' });
      return;
    }
    setExpandedSuggestionId(null);
    setDismissing(prev => ({ ...prev, [String(sug.inventoryId)]: sug }));
  };
  const closeDismissBar = (sug) => setDismissing(prev => {
    const next = { ...prev };
    delete next[String(sug.inventoryId)];
    return next;
  });
  // Optimistic: the suggestion leaves the table at once, rolls back on error
  const confirmDismiss = (sug, reasonCode) => {
    closeDismissBar(sug);
    // mutateAsync, not mutate(…, { onError }): per-call callbacks only fire
    // for the latest of several quick dismisses
    dismissMutation.mutateAsync({ id: sug.id, reasonCode: reasonCode || undefined })
      .then(res => reportDismiss(res, reasonCode), err => flashError(err, 'Dismiss failed'));
  };

  // Rating records feedback only — nothing is written to Skybox
  const saveRating = (sug, payload) => rateMutation.mutateAsync({ id: sug.id, ...payload });

  // Admin dismiss with a rating. The dismiss call stores the reason code (and
  // note) as this admin's "wrong" feedback; a price, a longer note or a
  // "right" verdict needs the full rating call on top — after the dismiss, so
  // the dismiss upsert can't blank the price.
  const dismissWithRating = async (sug, payload) => {
    const { verdict, reasonCode, preferredPrice, note } = payload;
    const res = await dismissMutation.mutateAsync({
      id: sug.id,
      reasonCode: verdict === 'wrong' ? reasonCode : undefined,
      note: note ? note.slice(0, 200) : undefined,
    });
    // The rating stands even if the dismiss didn't (feedback takes any status)
    if (verdict === 'right' || preferredPrice != null || (note && note.length > 200)) {
      await saveRating(sug, payload);
    }
    reportDismiss(res, verdict === 'wrong' ? reasonCode : null);
  };

  const openRatePopover = (sug, listing, anchorEl) => setRatePopover({ sug, listing, anchorEl: anchorEl || null, mode: 'rate' });

  const handlePopoverSave = async (payload) => {
    const { sug, mode } = ratePopover;
    if (mode === 'dismiss') return dismissWithRating(sug, payload);
    await saveRating(sug, payload);
    flash('Rating saved — nothing written to Skybox');
  };
  const handlePopoverSkip = async () => {
    const { sug } = ratePopover;
    reportDismiss(await dismissMutation.mutateAsync({ id: sug.id }), null);
  };
  // The only popover path that writes a price: rating first, then the normal
  // accept call with the chosen B
  const handlePopoverSaveAndApply = async (payload, price) => {
    const { sug, listing } = ratePopover;
    await saveRating(sug, payload);
    const r = listing ? await handleApplySuggestion(sug, listing, price) : await applySuggestionById(sug, price);
    if (!r.ok) throw new Error(`Rating saved, but the price was not applied: ${r.error}`);
  };

  // Bulk apply result: patch every successful item into the pricing cache in
  // one pass (no L price comes back from bulk; the poller below converges
  // them - one shared session for the whole batch), refresh AI + Dashboard
  // caches, remount rows.
  const handleBulkApplied = ({ results = [], errors = [] }) => {
    for (const r of results) confirmedPriceUpdates.current[String(r.inventoryId)] = r.newPrice;
    if (results.length) {
      setRowErrors(prev => {
        const next = { ...prev };
        for (const r of results) delete next[String(r.inventoryId)];
        return next;
      });
    }
    const prevById = new Map((queryClient.getQueryData(eventPricingKeys.detail(eventId))?.myInventory || [])
      .map(item => [String(item.id), item]));
    const byId = new Map(results.map(r => [String(r.inventoryId), r]));
    queryClient.setQueryData(eventPricingKeys.detail(eventId), (prev) => {
      if (!prev?.myInventory) return prev;
      return {
        ...prev,
        myInventory: prev.myInventory.map(item => {
          const r = byId.get(String(item.id));
          if (!r) return item;
          return {
            ...item,
            listPrice: r.newPrice,
            ...(r.unitPrice != null ? { unitPrice: r.unitPrice } : {}),
            lastPriceUpdate: new Date().toISOString(),
          };
        }),
      };
    });
    queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    queryClient.invalidateQueries({ queryKey: ['ai', 'event', eventId] });
    queryClient.invalidateQueries({ queryKey: ['ai', 'attention'] });
    // Same rule as applyPriceResult: pin Skybox's what-if L; rows not on Vivid
    // before this write poll for their first L, already-listed rows poll in
    // verify mode (one shared session for the whole batch; its first tick is
    // at +10 s, after Skybox's re-publish blip that the server guard also
    // covers).
    const toTrack = [];
    const toVerify = [];
    for (const r of results) {
      const prevItem = prevById.get(String(r.inventoryId));
      const wasListed = !!(prevItem?.unitPrice > 0 && prevItem?.vividListingId);
      if (r.unitPrice != null) lPoll.pin(r.inventoryId, r.unitPrice, r.newPrice);
      if (!(r.newPrice > 0)) continue;
      const entry = { id: r.inventoryId, expectedListPrice: r.newPrice, vividListingId: prevItem?.vividListingId };
      if (wasListed) toVerify.push({ ...entry, previousUnitPrice: prevItem.unitPrice, whatIf: r.unitPrice });
      else toTrack.push(entry);
    }
    if (toTrack.length) lPoll.track(toTrack);
    if (toVerify.length) lPoll.track(toVerify, { origin: 'verify' });
    setPriceUpdateKey(prev => prev + 1);
    if (results.length) { setSuccessMessage(`Applied ${results.length} price${results.length === 1 ? '' : 's'}${errors.length ? `, ${errors.length} failed` : ''}`); setTimeout(() => setSuccessMessage(''), 3000); }
  };

  // Bulk price modal result: same cache patching, plus drop applied rows from
  // the selection so the bar only counts what is still pending
  const handleBulkPriceApplied = (data) => {
    handleBulkApplied(data);
    const applied = data?.results || [];
    addApiLog('RESPONSE', {
      method: 'PUT', endpoint: '/api/inventory/bulk-price', status: 200,
      response: { updated: applied.length, failed: (data?.errors || []).length },
      skyboxApiCall: { endpoint: 'PUT https://skybox.vividseats.com/services/inventory', payload: `[${applied.length} × { id, listPrice }]` },
    });
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const r of applied) next.delete(String(r.inventoryId));
      return next;
    });
  };

  // A bulk request timed out / died mid-flight: the server may still be
  // applying the batch (nginx gives up before the backend does). Fetch fresh
  // prices so the modal can reconcile which rows actually landed — twice,
  // because the server had already been working for the whole proxy window
  // when the client learned anything, so writes can land well after +8 s.
  const handleBulkOutcomeUnknown = () => {
    addApiLog('ERROR', {
      method: 'PUT', endpoint: '/api/inventory/bulk-price', status: 'timeout / unknown outcome',
      error: 'Request timed out — server may still be applying; scheduling fresh reads at +8s and +45s to reconcile',
    });
    setTimeout(handleRefresh, 8000);
    setTimeout(handleRefresh, 45000);
  };

  // Merge modal closed: after any merge / undo, re-read pricing and the plan,
  // and forget selections, row errors and unsaved edits of listings now gone
  const handleMergeClose = ({ changed, consumedIds = [] } = {}) => {
    setShowMerge(false);
    if (!changed) return;
    queryClient.invalidateQueries({ queryKey: eventPricingKeys.detail(eventId) });
    queryClient.invalidateQueries({ queryKey: mergePlanKeys.detail(eventId) });
    const gone = new Set(consumedIds);
    const dropGone = (prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !gone.has(id)));
    setSelectedIds(prev => new Set([...prev].filter(id => !gone.has(id))));
    setRowErrors(dropGone);
    setEditingPrices(dropGone); // an unsaved B on a consumed listing would only be refused
  };

  // "Apply N changes": every typed-but-unsaved row in ONE bulk-price call, so
  // the whole batch costs a single rate-limit token instead of one per SET.
  // Chunks of 50 (the server's per-request cap) go out sequentially, and the
  // first chunk that fails stops the run — a rate limit or an unknown outcome
  // must not be followed by more writes.
  const applyAllEdits = async () => {
    const edits = pendingEdits;
    if (!edits.length || applyingAll) return;
    setApplyingAll(true);
    try {
      for (let i = 0; i < edits.length; i += 50) {
        const chunk = edits.slice(i, i + 50);
        const ids = chunk.map(e => e.id);
        setSavingPrices(prev => {
          const next = { ...prev };
          for (const id of ids) next[id] = true;
          return next;
        });
        addApiLog('REQUEST', {
          method: 'PUT',
          endpoint: '/api/inventory/bulk-price',
          payload: { updates: chunk.length },
        });
        let stop = false;
        try {
          const res = await bulkUpdatePrices(chunk.map(e => ({
            inventoryId: e.listing.id,
            newPrice: e.newPrice,
            // Stale-price guard: the server 409s a row whose B moved since we
            // read it, so a re-apply of an already-landed batch is a no-op
            expectedCurrentPrice: Number(e.listing.listPrice) || 0,
            suggestionId: suggestionsByInvId.get(e.id)?.id,
            vividListingId: e.listing.vividListingId || undefined,
            zone: !!e.listing.zone,
          })));
          handleBulkPriceApplied(res.data);
          const applied = new Set((res.data?.results || []).map(r => String(r.inventoryId)));
          setEditingPrices(prev => {
            const next = { ...prev };
            for (const id of applied) delete next[id];
            return next;
          });
          // Per-row rejections come back inside a 200 — they keep their typed
          // value and their own message.
          for (const bad of res.data?.errors || []) {
            putRowError(bad.inventoryId, {
              status: 400, code: bad.code || null, retryAfterSec: null, retryAt: null,
              attemptedPrice: chunk.find(e => e.id === String(bad.inventoryId))?.newPrice ?? null,
              message: bad.error || 'Not applied',
            });
          }
        } catch (err) {
          const info = describePriceError(err);
          stop = true;
          if (info.status === 'unknown') {
            // The server may still be applying this chunk: lock the rows and
            // let the reconcile effect below say what actually happened.
            setPendingOutcome(prev => {
              const next = { ...prev };
              for (const e of chunk) next[e.id] = { intendedPrice: e.newPrice, priorPrice: Number(e.listing.listPrice) || 0 };
              return next;
            });
            for (const e of chunk) {
              putRowError(e.id, { ...info, attemptedPrice: e.newPrice, retryAt: null, message: 'Timed out — checking whether the server applied this. Do NOT re-send.' });
            }
            handleBulkOutcomeUnknown();
          } else if (info.status === 429) {
            // One countdown for everything still unsent, including the chunks
            // this run will now skip.
            const retryAt = Date.now() + (info.retryAfterSec || 30) * 1000;
            for (const e of edits.slice(i)) putRowError(e.id, { ...info, attemptedPrice: e.newPrice, retryAt });
          } else {
            for (const e of chunk) putRowError(e.id, { ...info, attemptedPrice: e.newPrice, retryAt: null });
            addApiLog('ERROR', { method: 'PUT', endpoint: '/api/inventory/bulk-price', status: err.response?.status || 'Network Error', error: info.message });
          }
        } finally {
          setSavingPrices(prev => {
            const next = { ...prev };
            for (const id of ids) next[id] = false;
            return next;
          });
        }
        if (stop) break;
      }
    } finally {
      setApplyingAll(false);
    }
  };

  const discardEdits = () => { setEditingPrices({}); setRowErrors({}); };

  // Reconcile rows whose apply timed out against the fresh payload the
  // +8s/+45s refreshes bring in (mirrors BulkPriceModal): a row now at its
  // intended price DID apply, so report it upstream like a normal success.
  const bulkAppliedRef = useRef(null);
  bulkAppliedRef.current = handleBulkApplied;
  useEffect(() => {
    const ids = Object.keys(pendingOutcome);
    if (!ids.length) return;
    const liveById = new Map((rawPricingData?.myInventory || []).map(l => [String(l.id), l]));
    const reconciled = [];
    for (const id of ids) {
      const live = liveById.get(id);
      if (live && Math.abs((Number(live.listPrice) || 0) - pendingOutcome[id].intendedPrice) < 0.01) {
        reconciled.push({ inventoryId: live.id, oldPrice: pendingOutcome[id].priorPrice, newPrice: pendingOutcome[id].intendedPrice, unitPrice: null, success: true });
      }
    }
    if (!reconciled.length) return;
    setPendingOutcome(prev => {
      const next = { ...prev };
      for (const r of reconciled) delete next[String(r.inventoryId)];
      return next;
    });
    setEditingPrices(prev => {
      const next = { ...prev };
      for (const r of reconciled) delete next[String(r.inventoryId)];
      return next;
    });
    bulkAppliedRef.current?.({ results: reconciled, errors: [] });
  }, [rawPricingData, pendingOutcome]);

  // Backstop: rows still showing their old price ~60 s after a timeout really
  // did not apply — unlock them and say so (the server's 409 guard covers the
  // race where the write lands even later).
  useEffect(() => {
    const ids = Object.keys(pendingOutcome);
    if (!ids.length) return;
    const t = setTimeout(() => {
      setPendingOutcome({});
      setRowErrors(prev => {
        const next = { ...prev };
        for (const id of ids) {
          if (next[id]) next[id] = { ...next[id], message: 'Still at its old price — not applied. You can retry.' };
        }
        return next;
      });
    }, 60000);
    return () => clearTimeout(t);
  }, [pendingOutcome]);

  // directive: string, or { directive?, targets? } for initial pricing of the
  // named listings (the bulk bar's "Price with AI")
  const handleAnalyze = (directive) => {
    const initial = directive && typeof directive === 'object' && directive.targets?.length;
    analyzeMutation.mutate(directive, {
      onSuccess: (res) => {
        if (res.data?.skipped) {
          const msgs = {
            unchanged: 'Nothing changed since the last analysis — no charge',
            in_progress: 'An analysis for this event is already running',
            recent: 'Analyzed recently — skipped',
          };
          setSuccessMessage(msgs[res.data.reason] || 'Skipped');
          setTimeout(() => setSuccessMessage(''), 2500);
        } else if (res.data?.cost != null) {
          setSuccessMessage(`${initial ? 'Initial prices ready' : 'Analysis complete'} · $${Number(res.data.cost).toFixed(2)}`);
          setTimeout(() => setSuccessMessage(''), 2500);
          if (initial) { setShowBulkReview(true); setSelectedIds(new Set()); lastSelectClickRef.current = null; }
        }
      },
      onError: () => {},
    });
  };
  // "Price with AI" on the bulk bar: initial pricing for the ticked rows
  const [aiDirective, setAiDirective] = useState('');
  const [showAiDirective, setShowAiDirective] = useState(false);
  const handlePriceSelectedWithAi = () => {
    const targets = [...selectedIds].map(Number).filter(n => Number.isInteger(n) && n > 0).slice(0, 50);
    if (!targets.length) return;
    handleAnalyze({ targets, directive: aiDirective.trim() || undefined });
  };
  const handleAsk = async (q) => (await askMutation.mutateAsync(q)).data;

  // Refresh = a genuinely fresh Skybox read (the server honours maxAge, floor
  // 10 s) of both pricing and sales instead of the cached payloads (5 min /
  // 2 min), plus an immediate L-price check for anything still pending. A
  // failed fetch surfaces through the query's own error state, so nothing to
  // do here.
  const handleRefresh = () => {
    queryClient.fetchQuery({
      queryKey: eventSalesKeys.detail(eventId),
      queryFn: async () => (await getEventSales(eventId, { maxAge: 10000 })).data,
      staleTime: 0,
    }).catch(() => {});
    lPoll.pollNow();
    queryClient.fetchQuery({
      queryKey: eventPricingKeys.detail(eventId),
      queryFn: async () => (await getEventPricing(eventId, { params: { maxAge: 10000 }, timeout: 200000 })).data,
      staleTime: 0,
    }).catch(() => {});
    queryClient.invalidateQueries({ queryKey: mergePlanKeys.detail(eventId) });
  };

  const handlePriceSave = async (inventoryId, currentPrice) => {
    const newPrice = parseFloat(editingPrices[inventoryId]);

    if (isNaN(newPrice) || newPrice < 0) {
      setError('Invalid price');
      return;
    }

    if (newPrice === currentPrice) {
      setEditingPrices(prev => {
        const updated = { ...prev };
        delete updated[inventoryId];
        return updated;
      });
      return;
    }

    // Find the inventory item for logging
    const inventoryItem = pricingData?.myInventory?.find(i => String(i.id) === String(inventoryId));
    const startTime = Date.now();

    // Log the API request
    addApiLog('REQUEST', {
      method: 'PUT',
      endpoint: `/api/inventory/${inventoryId}/price`,
      payload: { newPrice },
      inventoryDetails: inventoryItem ? {
        section: inventoryItem.section || inventoryItem.displayName,
        row: inventoryItem.row,
        quantity: inventoryItem.quantity,
        currentBPrice: currentPrice,
        newBPrice: newPrice
      } : null
    });

    try {
      setSavingPrices(prev => ({ ...prev, [inventoryId]: true }));
      clearRowError(inventoryId); // retrying: drop the previous rejection
      // If this listing has a pending AI suggestion, tell the backend so it
      // records accepted (same price) / modified (different price) telemetry
      const relatedSuggestion = suggestionsByInvId.get(String(inventoryId));
      const response = await updatePrice(inventoryId, newPrice, {
        vividListingId: inventoryItem?.vividListingId,
        zone: inventoryItem?.zone,
        suggestionId: relatedSuggestion?.id,
        // Stale-price guard: reject (409) if this row's B moved since we read it
        expectedCurrentPrice: Number(currentPrice) || 0,
      });
      const duration = Date.now() - startTime;

      // Log successful response
      addApiLog('RESPONSE', {
        method: 'PUT',
        endpoint: `/api/inventory/${inventoryId}/price`,
        status: 200,
        duration: `${duration}ms`,
        response: response.data,
        skyboxApiCall: {
          endpoint: 'PUT https://skybox.vividseats.com/services/inventory',
          payload: `[{ id: ${inventoryId}, listPrice: ${newPrice} }]`
        }
      });

      applyPriceResult(inventoryId, newPrice, response.data.unitPrice);
      if (relatedSuggestion) {
        queryClient.invalidateQueries({ queryKey: ['ai', 'event', eventId] });
        queryClient.invalidateQueries({ queryKey: ['ai', 'attention'] });
      }
    } catch (err) {
      const duration = Date.now() - startTime;

      // Log error response
      addApiLog('ERROR', {
        method: 'PUT',
        endpoint: `/api/inventory/${inventoryId}/price`,
        status: err.response?.status || 'Network Error',
        duration: `${duration}ms`,
        error: err.response?.data?.error || err.message,
        skyboxApiCall: {
          endpoint: 'PUT https://skybox.vividseats.com/services/inventory',
          payload: `[{ id: ${inventoryId}, listPrice: ${newPrice} }]`
        }
      });

      // The typed value stays in the box with a sticky NOT SAVED marker — a
      // toast alone let rejected prices pass for applied ones.
      putRowError(inventoryId, rowErrorEntry(err, newPrice));
      setError(describePriceError(err).message);
      setTimeout(() => setError(''), 6000);
    } finally {
      setSavingPrices(prev => ({ ...prev, [inventoryId]: false }));
    }
  };

  const handlePriceKeyDown = (e, inventoryId, currentPrice) => {
    if (e.key === 'Enter') {
      // Ctrl/Cmd+Enter sends every unsaved row as one batch
      if (e.ctrlKey || e.metaKey) { applyAllEdits(); return; }
      handlePriceSave(inventoryId, currentPrice);
    } else if (e.key === 'Escape') {
      clearRowError(inventoryId);
      setEditingPrices(prev => {
        const updated = { ...prev };
        delete updated[inventoryId];
        return updated;
      });
    }
  };

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIndicator = (column) => {
    if (sortConfig.column !== column) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const clearFilters = () => {
    setSelectedSections([]);
    setQuantityFilter('any');
  };

  const getGroupName = (listing) => {
    const group = pricingData?.sectionGroups?.find(g => g.id === listing.groupId);
    return group?.name || '-';
  };

  const getFilteredListings = () => {
    if (!pricingData) return { mine: [], competitors: [] };

    // Debug: Log unique section names and groups from listings
    if (selectedSections.length > 0 && pricingData.myInventory?.length > 0) {
      const uniqueListingSections = [...new Set([
        ...(pricingData.myInventory || []).map(l => l.displayName || l.section),
        ...(pricingData.competitorListings || []).map(l => l.displayName || l.section)
      ])].filter(Boolean).sort();
      const availableGroups = pricingData.sectionGroups?.map(g => g.name) || [];
      console.log('=== SECTION NAME COMPARISON ===');
      console.log('Selected from map:', selectedSections.map(s => `${s.name} (${s.group || 'no group'})`));
      console.log('Available in listings:', uniqueListingSections.slice(0, 20));
      console.log('Available groups in Skybox:', availableGroups);
    }

    // =========================================================================
    // SECTION MATCHING (uses sectionId from Skybox API for direct chart mapping)
    // =========================================================================

    const matchesSection = (listing) => {
      if (selectedSections.length === 0) return true;

      return selectedSections.some(s => {
        // Primary: Match by sectionId (direct chart element mapping)
        if (listing.sectionId && s.id && String(listing.sectionId) === String(s.id)) return true;

        // Fallback for unmapped listings (sectionId=0): exact display name match only
        if (!listing.sectionId || listing.sectionId === 0) {
          const listingName = (listing.displayName || listing.section || '').toLowerCase().trim();
          const selectedName = (s.name || '').toLowerCase().trim();
          if (listingName && selectedName && listingName === selectedName) return true;
        }

        return false;
      });
    };

    const matchesQuantity = (qty) => {
      if (quantityFilter === 'any') return true;
      return qty >= parseInt(quantityFilter);
    };

    let mine = pricingData.myInventory || [];
    let competitors = pricingData.competitorListings || [];

    if (selectedSections.length > 0) {
      mine = mine.filter(l => matchesSection(l));
      competitors = competitors.filter(l => matchesSection(l));
    }

    mine = mine.filter(l => matchesQuantity(l.quantity));
    competitors = competitors.filter(l => matchesQuantity(l.quantity));

    return { mine, competitors };
  };

  // Tier + notes panel. Built before the early returns and rendered in every
  // branch (loading, pricing error, map loading, map missing, normal) because
  // notes are SQLite-backed and must stay usable when Skybox is down.
  const performerTier = notesData?.tier || null;
  const tierLabel = performerTier
    ? (['S', '1', '2', '3'].includes(performerTier) ? `Tier ${performerTier}` : performerTier)
    : 'No Tier';
  const notesUnsaved = eventNotesDraft.trim() !== serverEventNotes;
  const notesPanel = (
    <div className="event-notes-panel">
      <div className="notes-panel-header">
        <span className="notes-panel-performer">{notesData?.performerName || eventName}</span>
        <span className={`tier-badge ${performerTier ? `tier-${performerTier.toLowerCase()}` : 'tier-none'}`}>
          {tierLabel}
        </span>
      </div>
      <div className="notes-panel-section">
        <div className="notes-panel-label">Tour Notes</div>
        <div className={`notes-panel-readonly${notesData?.generalNotes ? '' : ' empty'}`}>
          {notesData?.generalNotes || 'No general notes'}
        </div>
      </div>
      <div className="notes-panel-section">
        <div className="notes-panel-label">Event Notes</div>
        <textarea
          className="notes-panel-textarea"
          value={eventNotesDraft}
          onChange={(e) => setEventNotesDraft(e.target.value)}
          placeholder="Add notes for this event..."
          maxLength={5000}
        />
        <button
          className="btn-primary btn-sm"
          onClick={handleSaveNotes}
          disabled={saveNotesMutation.isPending || !notesUnsaved}
        >
          {saveNotesMutation.isPending ? 'Saving...' : 'Save Notes'}
        </button>
        {notesData?.eventNote?.updatedBy && (
          <div className="notes-panel-meta">
            Last updated by {notesData.eventNote.updatedBy}
            {notesData.eventNote.updatedAt ? ` · ${formatDateTimeEastern(notesData.eventNote.updatedAt)}` : ''}
          </div>
        )}
      </div>
    </div>
  );

  const tmPanel = <TicketMetricPanel tm={tm} role={user?.role} />;

  if (loading) {
    return (
      <div className="compare-page">
        <div className="compare-loading">Loading pricing data...</div>
        <div className="tm-loading-slot">{tmPanel}</div>
        <div style={{ maxWidth: '400px', margin: '1rem auto 0' }}>
          {notesPanel}
        </div>
      </div>
    );
  }

  if ((error || pricingQueryError) && !pricingData) {
    return (
      <div className="compare-page">
        {successMessage && <div className="success-toast">{successMessage}</div>}
        <div className="compare-error">
          <p>{pricingQueryError || error}</p>
          {pricingErrorRetryable && (
            <button onClick={() => refetchPricing()} disabled={loading}>
              {loading ? 'Retrying…' : 'Retry'}
            </button>
          )}
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
        <div className="tm-loading-slot">{tmPanel}</div>
        <div style={{ maxWidth: '400px', margin: '1rem auto 0' }}>
          {notesPanel}
        </div>
      </div>
    );
  }

  const { mine, competitors } = getFilteredListings();

  // Apply confirmed price updates to ensure UI always shows the latest confirmed
  // prices, and overlay a pinned L (Skybox's what-if / last confirmed value)
  // while the cached row still carries unitPrice 0 for that same B.
  const mineWithConfirmedPrices = mine.map(l => {
    const confirmedPrice = confirmedPriceUpdates.current[String(l.id)];
    const row = confirmedPrice !== undefined ? { ...l, listPrice: confirmedPrice } : l;
    const { unitPrice, pinned } = lPoll.overlay(row);
    return pinned ? { ...row, unitPrice, lPricePinned: true } : row;
  });

  let allListings = [
    ...mineWithConfirmedPrices.map(l => ({ ...l, owner: 'You' })),
    ...competitors.map(l => ({ ...l, owner: 'Competitor' }))
  ];

  const sortListings = (listings) => {
    if (!sortConfig.column) {
      return listings.sort((a, b) => (a.unitPrice || 0) - (b.unitPrice || 0));
    }

    return listings.sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.column) {
        case 'group':
          aVal = getGroupName(a).toLowerCase();
          bVal = getGroupName(b).toLowerCase();
          break;
        case 'section':
          aVal = (a.displayName || a.section || '').toLowerCase();
          bVal = (b.displayName || b.section || '').toLowerCase();
          break;
        case 'row':
          aVal = a.row || '';
          bVal = b.row || '';
          break;
        case 'seats':
          aVal = a.lowSeat || 0;
          bVal = b.lowSeat || 0;
          break;
        case 'qty':
          aVal = a.quantity || 0;
          bVal = b.quantity || 0;
          break;
        case 'cost':
          aVal = a.unitCostAverage || 0;
          bVal = b.unitCostAverage || 0;
          break;
        case 'bprice':
          aVal = a.listPrice || 0;
          bVal = b.listPrice || 0;
          break;
        case 'lprice':
          aVal = a.unitPrice || 0;
          bVal = b.unitPrice || 0;
          break;
        case 'suggested':
          // Rows without a suggestion sink to the bottom regardless of direction
          aVal = suggestionsByInvId.get(String(a.id))?.suggestedPrice ?? (sortConfig.direction === 'asc' ? Infinity : -Infinity);
          bVal = suggestionsByInvId.get(String(b.id))?.suggestedPrice ?? (sortConfig.direction === 'asc' ? Infinity : -Infinity);
          break;
        case 'updated':
          aVal = a.lastPriceUpdate ? new Date(a.lastPriceUpdate).getTime() : 0;
          bVal = b.lastPriceUpdate ? new Date(b.lastPriceUpdate).getTime() : 0;
          break;
        case 'purchased':
          aVal = a.createdDate ? new Date(a.createdDate).getTime() : 0;
          bVal = b.createdDate ? new Date(b.createdDate).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const result = aVal.localeCompare(bVal, undefined, { numeric: true });
        return sortConfig.direction === 'asc' ? result : -result;
      }

      const result = aVal - bVal;
      return sortConfig.direction === 'asc' ? result : -result;
    });
  };

  if (showMyListingsFirst) {
    const myListings = sortListings(allListings.filter(l => l.owner === 'You'));
    const otherListings = sortListings(allListings.filter(l => l.owner !== 'You'));
    allListings = [...myListings, ...otherListings];
  } else {
    allListings = sortListings(allListings);
  }

  // Section, Row, Seats, Qty, [Cost], B Price, [Suggested], L Price, Updated, Purchased, Notes, Tags
  const columnCount = 11 + (user.canSeeCost ? 1 : 0) + (showAiColumn ? 1 : 0);

  // What the server will check this user's writes against (null = unlimited)
  const limitsHint = priceLimitsHint(user);

  // Select-all-visible covers my rows after the section/qty filters — so the
  // map selection doubles as the bulk selector
  const visibleMineIds = allListings.filter(l => l.owner === 'You').map(l => String(l.id));
  const allVisibleMineSelected = visibleMineIds.length > 0 && visibleMineIds.every(id => selectedIds.has(id));
  const toggleSelectAllVisible = () => setSelectedIds(prev => {
    const next = new Set(prev);
    if (allVisibleMineSelected) visibleMineIds.forEach(id => next.delete(id));
    else visibleMineIds.forEach(id => next.add(id));
    return next;
  });
  const toggleSelected = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    const key = String(id);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  // Shift+click selects the range between the last clicked row and this one
  // (your rows only, in displayed order). The target state follows the box
  // that was clicked, so shift-unticking clears a range too. An anchor that
  // the filters have since hidden falls back to a plain single toggle.
  const handleSelectClick = (e, id) => {
    const key = String(id);
    const anchor = lastSelectClickRef.current;
    const shift = !!e.nativeEvent?.shiftKey;
    const anchorIdx = anchor ? visibleMineIds.indexOf(anchor) : -1;
    const clickIdx = visibleMineIds.indexOf(key);
    if (shift && anchorIdx !== -1 && clickIdx !== -1) {
      const target = !selectedIds.has(key);
      const [from, to] = anchorIdx < clickIdx ? [anchorIdx, clickIdx] : [clickIdx, anchorIdx];
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (let i = from; i <= to; i++) {
          if (target) next.add(visibleMineIds[i]); else next.delete(visibleMineIds[i]);
        }
        return next;
      });
    } else {
      toggleSelected(key);
    }
    lastSelectClickRef.current = key;
  };
  const selectedListings = applyConfirmedPriceUpdates(pricingData?.myInventory || [])
    .filter(l => selectedIds.has(String(l.id)));

  // L Price cell. Own rows never show a fake "$0.00": a priced row whose Vivid
  // listing Skybox hasn't published yet shows a pending marker (the poller is
  // on it), an unpriced row shows "not on Vivid yet".
  const renderLPrice = (listing) => {
    if (listing.owner !== 'You' || listing.unitPrice > 0) {
      if (listing.lPricePinned) {
        // Repriced while already on Vivid: the value is Skybox's projection
        // until the poller sees Vivid's real L move (dotted), and a warning
        // once the verify window passed without Vivid taking the price.
        const v = lPoll.verifyState(listing.id);
        const prevL = v?.previousUnitPrice > 0 ? formatCurrency(v.previousUnitPrice) : 'its old price';
        if (v?.status === 'stale') {
          const mins = Math.max(1, Math.round((Date.now() - v.since) / 60000));
          return (
            <span
              className="lprice-stale"
              title={`Vivid still shows ${prevL} (set ${mins} min ago). Skybox hasn't pushed this price to Vivid - check Skybox, or Refresh to re-check.`}
            >
              {formatCurrency(listing.unitPrice)}<span className="lprice-warn">⚠</span>
            </span>
          );
        }
        if (v?.status === 'verifying') {
          return (
            <span
              className="lprice-projected"
              title={`Skybox's projected L for ${formatCurrency(listing.listPrice)} - Vivid still shows ${prevL}; checking that it updates…`}
            >
              {formatCurrency(listing.unitPrice)}
            </span>
          );
        }
        return (
          <span title="Skybox's calculated L price for this B price - Vivid listing still publishing">
            {formatCurrency(listing.unitPrice)}
          </span>
        );
      }
      return formatCurrency(listing.unitPrice);
    }
    if (listing.listPrice > 0) {
      const tracked = lPoll.pendingIds.has(String(listing.id));
      return (
        <span
          className="lprice-pending"
          title={tracked
            ? "Skybox hasn't published the Vivid listing yet - checking automatically"
            : 'No L price from Skybox yet - click Refresh to check again'}
        >…</span>
      );
    }
    return <span className="lprice-notlisted" title="Not on Vivid yet - set a B price">—</span>;
  };

  // Sales header stats. totalSold/totalInvoices cover ALL invoices; fall back
  // to summing the visible recent-sales list if a cached response predates
  // those fields (or the backend hit a Skybox error and returned nulls).
  const recentSalesSum = salesData?.sales?.reduce((sum, sale) => sum + (sale.quantity || 0), 0) || 0;
  const seatsSold = typeof salesData?.totalSold === 'number' ? salesData.totalSold : recentSalesSum;
  const invoiceCount = typeof salesData?.totalInvoices === 'number'
    ? salesData.totalInvoices
    : (salesData?.sales?.length || 0);
  const seatsRemaining = pricingData?.myInventory?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
  const pctSold = (seatsSold + seatsRemaining) > 0
    ? Math.round((seatsSold / (seatsSold + seatsRemaining)) * 100)
    : 0;
  const mergeBtn = mergeButtonState(mergePlan);

  return (
    <div className="compare-page-vertical">
      {/* Header */}
      <div className="compare-header">
        <button className="back-btn" onClick={() => navigate(-1)}>&larr; Back</button>
        <div className="header-title">
          <h1>{eventName}</h1>
          <div className="event-details">
            {eventDate && <span className="event-date">{formatEventDate(eventDate)}</span>}
            {venue && (
              <span className="event-venue">
                {venue}{venueCity && `, ${venueCity}`}{venueState && `, ${venueState}`}
              </span>
            )}
          </div>
        </div>
        <div className="header-actions">
          {user?.skyboxSession && user.skyboxSession !== 'ok' && (
            <span
              className="skybox-session-warn"
              title="Ask a super admin to renew your Skybox login in Admin → Users. Until then Skybox history shows your price changes as Prime Lion Tickets."
            >
              Skybox session {user.skyboxSession === 'dead' ? 'dead' : 'not set'} — changes show as Prime Lion Tickets
            </span>
          )}
          <button
            className="btn-secondary"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh prices and sales from Skybox"
          >
            {loading ? 'Refreshing...' : '↻ Refresh'}
          </button>
          <a className="btn-secondary" href={siblingPageUrl('/lion-auto', eventId, searchParams)} target="_blank" rel="noopener noreferrer">Lion Auto</a>
          {vividSeatsUrl && (
            <a href={vividSeatsUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              Open VividSeats
            </a>
          )}
        </div>
      </div>

      {successMessage && <div className="success-toast">{successMessage}</div>}
      {error && <div className="error-toast">{error}</div>}

      {/* Map Rail layout: map + groups + notes docked left, table gets the remaining width */}
      <div className="compare-body-split">
        <div className="compare-left-rail">
          {/* Map Section: seat map with toolbar, then the selection bar */}
          <div className="compare-map-section">
            {mapLoading && <div className="map-loading">Loading map...</div>}

            {svgContent && chartData && !mapLoading && (
              <>
                <CompareSeatMap
                  svgContent={svgContent}
                  chartData={chartData}
                  selectedSections={selectedSections}
                  onToggleSection={toggleSection}
                  availableSectionIds={availableSectionIds}
                />

                {/* Selected sections info */}
                {selectedSections.length > 0 && (
                  <div className="selected-sections-bar">
                    <span>Selected: {selectedSections.length} section{selectedSections.length > 1 ? 's' : ''}</span>
                    <button className="clear-btn" onClick={clearFilters}>Clear All</button>
                  </div>
                )}
              </>
            )}

            {!svgContent && !mapLoading && (
              <div className="map-placeholder">Map not available</div>
            )}
          </div>

          {/* Group selector panel */}
          {svgContent && chartData && !mapLoading && (
            <div className="group-selector-panel">
              <div className="group-selector-header">
                <span>Section Groups</span>
                {selectedSections.length > 0 && (
                  <button className="reset-btn" onClick={clearFilters}>Reset</button>
                )}
              </div>
              <div className="group-selector-list">
                {getChartGroups().map(group => (
                  <div
                    key={group.id}
                    className={`group-selector-item ${isGroupSelected(group) ? 'selected' : ''} ${isGroupPartiallySelected(group) ? 'partial' : ''}`}
                    onClick={() => toggleGroup(group)}
                  >
                    <span
                      className="group-color-dot"
                      style={{ backgroundColor: group.color }}
                    />
                    <span className="group-checkbox">
                      {isGroupSelected(group) ? '✓' : isGroupPartiallySelected(group) ? '−' : ''}
                    </span>
                    <span className="group-name">{group.name}</span>
                    <span className="group-section-count">{group.sections.length}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tier + notes panel */}
          {notesPanel}
        </div>

        <div className="compare-main-col">

      {/* AI Assist strip — only when the assistant is enabled */}
      {ai && (
        <AiEventPanel
          ai={ai}
          pendingCount={pendingSuggestionCount}
          onAnalyze={handleAnalyze}
          analyzing={analyzeMutation.isPending}
          analyzeProgress={analyzeMutation.progress}
          analyzeError={analyzeMutation.error ? (analyzeMutation.error.response?.data?.error || analyzeMutation.error.message) : null}
          onAsk={handleAsk}
          asking={askMutation.isPending}
          onOpenBulkReview={() => setShowBulkReview(true)}
        />
      )}
      {/* TicketMetric sold strip — shows whether or not the AI is on */}
      {tmPanel}
      {showBulkReview && ai && (
        <AiBulkReviewModal
          suggestions={[...suggestionsByInvId.values()]}
          latestByInventory={latestByInventory}
          myInventory={pricingData?.myInventory}
          isAdmin={isAdmin}
          onClose={() => setShowBulkReview(false)}
          onApplied={handleBulkApplied}
          onDismiss={(sug, reasonCode) => dismissMutation.mutateAsync({ id: sug.id, reasonCode: reasonCode || undefined, source: 'bulk_dismiss' })}
          onRate={saveRating}
          onApplySuggestion={applySuggestionById}
        />
      )}
      {ratePopover && (
        <AiRatePopover
          key={`${ratePopover.mode}-${ratePopover.sug.id}`}
          suggestion={ratePopover.sug}
          anchorEl={ratePopover.anchorEl}
          isAdmin={isAdmin}
          mode={ratePopover.mode}
          onSave={handlePopoverSave}
          onSaveAndApply={ratePopover.mode === 'rate' ? handlePopoverSaveAndApply : undefined}
          onSkip={ratePopover.mode === 'dismiss' ? handlePopoverSkip : undefined}
          onClose={() => setRatePopover(null)}
        />
      )}
      {showBulkPrice && (
        <BulkPriceModal
          listings={selectedListings}
          suggestionsByInvId={suggestionsByInvId}
          onClose={() => setShowBulkPrice(false)}
          onApplied={handleBulkPriceApplied}
          onOutcomeUnknown={handleBulkOutcomeUnknown}
        />
      )}
      {showMerge && <MergeListingsModal eventId={eventId} onClose={handleMergeClose} returnFocusRef={mergeButtonRef} />}

      {/* Sales Section */}
      <div className="compare-sales-section">
        <div
          className="sales-header"
          onClick={() => setShowSales(!showSales)}
          style={{ cursor: 'pointer' }}
        >
          <span className="sales-toggle">{showSales ? '▼' : '▶'}</span>
          <span>Sales ({invoiceCount}) — Seats Sold: {seatsSold} | Seats Remaining: {seatsRemaining} | {pctSold}% Sold</span>
          {salesLoading && <span className="sales-loading">Loading...</span>}
        </div>
        {showSales && (
          <div className="sales-content">
            {salesData?.sales?.length > 0 ? (
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Invoice Date</th>
                    <th>Section</th>
                    <th>Row</th>
                    <th>Seats</th>
                    <th>Qty</th>
                    <th title="B price at the time of sale">B Price</th>
                    <th>Sold Total</th>
                    <th>Unit Total</th>
                  </tr>
                </thead>
                <tbody>
                  {salesData.sales.map((sale) => (
                    <tr key={sale.invoiceId}>
                      <td>{formatDateTimeEastern(sale.invoiceDate, { year: undefined })}</td>
                      <td>{sale.section || '-'}</td>
                      <td>{sale.row || '-'}</td>
                      <td>{sale.seats || '-'}</td>
                      <td>{sale.quantity}</td>
                      <td>{sale.listPrice != null ? `$${sale.listPrice.toFixed(2)}` : '-'}</td>
                      <td>${sale.soldAmount?.toFixed(2) || '0.00'}</td>
                      <td>${sale.quantity > 0 ? (sale.soldAmount / sale.quantity).toFixed(2) : '0.00'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="sales-empty">No recent sales found</div>
            )}
            {invoiceCount > (salesData?.sales?.length || 0) && (
              <div className="sales-note">Showing {salesData?.sales?.length || 0} most recent of {invoiceCount} sales</div>
            )}
          </div>
        )}
      </div>

      {/* Data Table Section */}
      <div className="compare-table-section">
        {/* Stats Bar */}
        <div className="compare-stats-bar">
          <span className="stat-badge mine">Your Listings: {pricingData?.totalMyListings || 0}</span>
          <span className="stat-badge competitor">Competitors: {pricingData?.totalCompetitorListings || 0}</span>
          <div className="filter-group">
            <label>Qty:</label>
            <select value={quantityFilter} onChange={(e) => setQuantityFilter(e.target.value)}>
              <option value="any">Any</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
              <option value="5">5+</option>
              <option value="6">6+</option>
            </select>
          </div>
          <label className="checkbox-filter">
            <input
              type="checkbox"
              checked={showMyListingsFirst}
              onChange={(e) => setShowMyListingsFirst(e.target.checked)}
            />
            Show My Listings First
          </label>
          <button ref={mergeButtonRef} type="button" className="btn-secondary btn-sm" onClick={() => setShowMerge(true)} disabled={mergeBtn.disabled} title={mergeBtn.title}>
            {mergeBtn.label}
          </button>
          {lPoll.staleIds.size > 0 && (
            <button
              type="button"
              className="stat-badge warn"
              onClick={handleRefresh}
              title="Skybox hasn't pushed these prices to Vivid within ~17 minutes. Click to re-check."
            >
              ⚠ {lPoll.staleIds.size} price{lPoll.staleIds.size === 1 ? '' : 's'} not on Vivid yet
            </button>
          )}
          {pendingEdits.length > 0 && (
            <span className="bulk-select-bar">
              <span className="bulk-select-count">{pendingEdits.length} unsaved</span>
              <button className="btn-primary btn-sm" onClick={applyAllEdits} disabled={applyingAll}>
                {applyingAll ? 'Applying…' : `Apply ${pendingEdits.length} change${pendingEdits.length === 1 ? '' : 's'}`}
              </button>
              <button className="btn-secondary btn-sm" onClick={discardEdits} disabled={applyingAll}>Discard</button>
            </span>
          )}
          {selectedIds.size > 0 && (
            <span className="bulk-select-bar">
              <span className="bulk-select-count">{selectedIds.size} selected</span>
              <button className="btn-primary btn-sm" onClick={() => setShowBulkPrice(true)}>Bulk update…</button>
              {ai && (
                <>
                  <button
                    className="btn-primary btn-sm"
                    onClick={handlePriceSelectedWithAi}
                    disabled={analyzeMutation.isPending || selectedIds.size > 50}
                    title={selectedIds.size > 50
                      ? 'At most 50 listings per run'
                      : 'The assistant proposes a first B price for each ticked listing (unpriced rows included) from the section comps, our history and TicketMetric sold data (pulled directly). 30–120s, then a review table opens. Nothing is written until you apply.'}
                  >
                    {analyzeMutation.isPending ? `Pricing… ${progressLabel(analyzeMutation.progress)}` : `Price with AI (${selectedIds.size})`}
                  </button>
                  <label className="checkbox-filter" title={'Add an instruction for the assistant (e.g. "price to move", "hold premium rows high")'}>
                    <input type="checkbox" checked={showAiDirective} onChange={e => setShowAiDirective(e.target.checked)} />
                    instruction
                  </label>
                  {showAiDirective && (
                    <input
                      type="text"
                      className="ai-ask-input"
                      style={{ maxWidth: 320 }}
                      value={aiDirective}
                      onChange={e => setAiDirective(e.target.value)}
                      placeholder='e.g. "price to sell steadily, we have 8 months"'
                      maxLength={500}
                    />
                  )}
                </>
              )}
              <button className="btn-secondary btn-sm" onClick={() => { setSelectedIds(new Set()); lastSelectClickRef.current = null; }}>Clear</button>
            </span>
          )}
        </div>

        {/* Listings Table */}
        <div className="compare-table-container">
          <table className="compare-table">
            <colgroup>
              <col className="col-select" />
              <col className="col-section" />
              <col className="col-row" />
              <col className="col-seats" />
              <col className="col-qty" />
              {user.canSeeCost && <col className="col-cost" />}
              <col className="col-bprice" />
              {showAiColumn && <col className={`col-suggested${isAdmin ? ' col-suggested-admin' : ''}`} />}
              <col className="col-lprice" />
              <col className="col-updated" />
              <col className="col-notes" />
            </colgroup>
            <thead>
              <tr>
                <th className="th-select">
                  <input
                    type="checkbox"
                    checked={allVisibleMineSelected}
                    disabled={visibleMineIds.length === 0}
                    onChange={toggleSelectAllVisible}
                    title="Select all of your listings currently shown"
                  />
                </th>
                <th className="sortable" onClick={() => handleSort('section')}>Section{getSortIndicator('section')}</th>
                <th className="sortable" onClick={() => handleSort('row')}>Row{getSortIndicator('row')}</th>
                <th className="sortable" onClick={() => handleSort('seats')}>Seats{getSortIndicator('seats')}</th>
                <th className="sortable" onClick={() => handleSort('qty')}>Qty{getSortIndicator('qty')}</th>
                {user.canSeeCost && <th className="sortable" onClick={() => handleSort('cost')}>Cost{getSortIndicator('cost')}</th>}
                <th className="sortable" onClick={() => handleSort('bprice')}>B Price{getSortIndicator('bprice')}</th>
                {showAiColumn && (
                  <th className="sortable th-suggested" onClick={() => handleSort('suggested')} title={`AI suggested B price — ✓ apply, click price to edit, × dismiss${isAdmin ? ', ★ rate (never writes to Skybox)' : ''}`}>
                    Suggested{getSortIndicator('suggested')}
                  </th>
                )}
                <th className="sortable" onClick={() => handleSort('lprice')}>L Price{getSortIndicator('lprice')}</th>
                <th className="sortable" onClick={() => handleSort('updated')}>Updated{getSortIndicator('updated')}</th>
                <th className="sortable" onClick={() => handleSort('purchased')}>Purchased{getSortIndicator('purchased')}</th>
                <th>Notes</th>
                <th>Tags</th>
              </tr>
            </thead>
            <tbody key={`tbody-${priceUpdateKey}`}>
              {allListings.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="no-data">No listings found</td>
                </tr>
              ) : (
                allListings.map((listing, idx) => {
                  const sug = listing.owner === 'You' ? suggestionsByInvId.get(String(listing.id)) : null;
                  const rowError = listing.owner === 'You' ? rowErrors[String(listing.id)] : null;
                  const rowFlags = listing.owner === 'You' ? (flagsByInvId.get(String(listing.id)) || []) : [];
                  const isExpanded = sug && expandedSuggestionId === String(listing.id);
                  const dismissingSug = sug && dismissing[String(listing.id)]?.id === sug.id ? sug : null;
                  const ladderText = isExpanded ? ladderLine(sug.ladder, sug.action === 'hold' ? null : sug.suggestedPrice) : '';
                  // Admins: the listing's latest suggestion once it is no longer live
                  const latestSug = isAdmin && !sug && listing.owner === 'You' ? latestByInventory[String(listing.id)] || null : null;
                  const rowColor = getListingColor(listing);
                  // My rows: tint the whole row with the section-group color
                  // (falls back to the green my-row tint when no color resolves)
                  const myTint = listing.owner === 'You' && rowColor ? hexToRgba(rowColor, 0.16) : null;
                  const rowStyle = myTint
                    ? { '--row-tint': myTint, '--row-tint-hover': hexToRgba(rowColor, 0.26) }
                    : undefined;
                  return (
                  <React.Fragment key={`${listing.id || idx}-${listing.listPrice}`}>
                  <tr
                    className={`${listing.owner === 'You' ? 'my-row' : 'competitor-row'}${sug ? ' has-suggestion' : ''}${isExpanded ? ' suggestion-expanded' : ''}`}
                    style={rowStyle}
                  >
                    <td className="select-cell">
                      {listing.owner === 'You' && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(String(listing.id))}
                          onChange={(e) => handleSelectClick(e, listing.id)}
                          onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
                        />
                      )}
                    </td>
                    <td>
                      {rowColor && (
                        <span className="section-group-dot" style={{ backgroundColor: rowColor }} title={getGroupName(listing) === '-' ? undefined : getGroupName(listing)} />
                      )}
                      {listing.displayName || listing.section || '-'}
                      {rowFlags.length > 0 && (
                        <span className="row-flags">
                          {rowFlags.map(f => (
                            <span key={f.code} className={`ai-flag-dot sev-${f.severity}`} title={`${FLAG_LABELS[f.code] || f.code} — ${FLAG_HELP[f.code] || ''}`} />
                          ))}
                        </span>
                      )}
                    </td>
                    <td>{listing.row || '-'}</td>
                    <td>
                      {listing.lowSeat && listing.highSeat
                        ? `${listing.lowSeat}-${listing.highSeat}`
                        : listing.seatNumbers || '-'}
                    </td>
                    <td>{listing.quantity}</td>
                    {user.canSeeCost && (
                      <td className="cost-cell">
                        {listing.owner === 'You' ? formatCurrency(listing.unitCostAverage) : '-'}
                      </td>
                    )}
                    <td className="bprice-cell">
                      {listing.owner === 'You' ? (
                        <>
                          <div className="editable-price">
                            <input
                              key={`price-input-${listing.id}-${listing.listPrice}`}
                              type="number"
                              value={editingPrices[listing.id] !== undefined
                                ? editingPrices[listing.id]
                                : (listing.listPrice || '')}
                              onChange={(e) => handlePriceChange(listing.id, e.target.value)}
                              onKeyDown={(e) => handlePriceKeyDown(e, listing.id, listing.listPrice)}
                              disabled={savingPrices[listing.id]}
                              className={`price-edit-input${rowError ? ' has-error' : ''}`}
                              title={limitsHint || undefined}
                              step="1"
                              min="0"
                            />
                            {editingPrices[listing.id] !== undefined &&
                             editingPrices[listing.id] !== String(listing.listPrice) && (
                              <button
                                className="set-btn"
                                onClick={() => handlePriceSave(listing.id, listing.listPrice)}
                                disabled={savingPrices[listing.id]}
                              >
                                SET
                              </button>
                            )}
                          </div>
                          {rowError && (
                            <div className="price-row-error">
                              <span className="price-unsaved-chip">NOT SAVED</span>
                              <span>{rowError.message}</span>
                              {rowError.retryAt ? <RetryCountdown until={rowError.retryAt} /> : null}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="competitor-price">-</span>
                      )}
                    </td>
                    {showAiColumn && (
                      <td className={`suggested-cell${sug?.status === 'stale' || ai?.stale ? ' suggest-stale' : ''}`}>
                        {sug ? (
                          <div className="suggest-wrap">
                            <span
                              className={`suggest-price act-${sug.action}`}
                              title={`${sug.reasoning}\n\nClick to load into the B Price box and adjust before setting.`}
                              onClick={() => handlePriceChange(listing.id, String(sug.suggestedPrice))}
                            >
                              {sug.action === 'hold' ? 'hold' : formatCurrency(sug.suggestedPrice)}
                            </span>
                            <button
                              className={`conf-chip conf-${sug.confidence}`}
                              title={`${sug.confidence} confidence — click for reasoning`}
                              onClick={() => setExpandedSuggestionId(isExpanded ? null : String(listing.id))}
                            >
                              {(sug.confidence || '?')[0].toUpperCase()}
                            </button>
                            {sug.action !== 'hold' && (
                              <button
                                className="suggest-apply-btn"
                                title={`Apply $${sug.suggestedPrice}`}
                                disabled={savingPrices[listing.id]}
                                onClick={() => handleApplySuggestion(sug, listing)}
                              >✓</button>
                            )}
                            <button
                              className={`suggest-dismiss-btn${dismissingSug ? ' active' : ''}`}
                              title={isAdmin ? 'Dismiss suggestion (asks why)' : 'Dismiss suggestion'}
                              onClick={(e) => handleDismissSuggestion(sug, listing, e.currentTarget)}
                            >×</button>
                            {isAdmin && (
                              <button
                                className={`suggest-rate-btn${sug.feedback?.mine ? ' rated' : ''}`}
                                title={sug.feedback?.mine
                                  ? `Your rating: ${feedbackLine(sug.feedback.mine)} — click to edit (never writes to Skybox)`
                                  : 'Rate this suggestion (never writes to Skybox)'}
                                onClick={(e) => openRatePopover(sug, listing, e.currentTarget)}
                              >
                                {sug.feedback?.mine ? 'rated ✓' : '★ Rate'}
                              </button>
                            )}
                          </div>
                        ) : latestSug ? (
                          <button
                            type="button"
                            className={`ai-chip ai-chip-latest${latestSug.feedback?.mine ? ' rated' : ''}`}
                            title={`AI suggested ${latestSug.action === 'hold' ? 'hold' : shortMoney(latestSug.suggestedPrice)}${latestSug.createdAt ? ` ${timeAgo(parseUtcDate(latestSug.createdAt))}` : ''} · ${latestSug.status || '?'}${latestSug.actedAt ? ` ${timeAgo(parseUtcDate(latestSug.actedAt))}` : ''}${latestSug.feedback?.mine ? `\nYour rating: ${feedbackLine(latestSug.feedback.mine)}` : ''}\nClick to rate (never writes to Skybox)`}
                            onClick={(e) => openRatePopover(latestSug, listing, e.currentTarget)}
                          >
                            {latestStatusText(latestSug)} · {latestSug.feedback?.mine ? 'rated ✓' : 'Rate'}
                          </button>
                        ) : (
                          <span className="competitor-price">-</span>
                        )}
                      </td>
                    )}
                    <td className="lprice-cell">{renderLPrice(listing)}</td>
                    <td className="updated-cell">
                      {listing.owner === 'You' && listing.lastPriceUpdate
                        ? formatDateTimeEastern(listing.lastPriceUpdate, { hour12: true })
                        : '-'}
                      {(() => {
                        const ph = listing.owner === 'You' ? historyByInvId[String(listing.id)] : null;
                        return ph && ph.repriceCount > 0 ? (
                          <span className="reprice-chip" title={historyLine(ph)}>×{ph.repriceCount}</span>
                        ) : null;
                      })()}
                    </td>
                    <td className="purchased-cell">
                      {listing.owner === 'You' && listing.createdDate
                        ? formatDateEastern(listing.createdDate)
                        : '-'}
                    </td>
                    <td className="notes-cell">{listing.publicNotes || '-'}</td>
                    <td className="tags-cell">
                      {(() => {
                        if (listing.owner !== 'You') return '-';
                        const tags = Array.isArray(listing.tags) ? listing.tags : (listing.tags ? listing.tags.split(' ').filter(Boolean) : []);
                        return tags.length > 0
                          ? tags.map((tag, i) => {
                              const color = tagColors[tag];
                              return (
                                <span
                                  key={i}
                                  className="tag-chip"
                                  style={color ? { backgroundColor: color, color: '#fff', borderColor: color } : {}}
                                >
                                  {tag}
                                </span>
                              );
                            })
                          : '-';
                      })()}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="reasoning-row">
                      <td colSpan={columnCount}>
                        <div className="reasoning-body">
                          <div className="reasoning-head">
                            <span className={`conf-chip conf-${sug.confidence}`}>{(sug.confidence || '?')[0].toUpperCase()}</span>
                            <strong>{sug.action === 'hold' ? 'Hold' : `${sug.action === 'lower' ? 'Lower' : 'Raise'} to ${formatCurrency(sug.suggestedPrice)}`}</strong>
                            <span className="reasoning-meta">
                              from {formatCurrency(sug.currentPrice)} · {sug.confidence} confidence{sug.createdAt ? ` · ${timeAgo(sug.createdAt.replace(' ', 'T') + 'Z')}` : ''}
                              {sug.status === 'stale' ? ' · STALE' : ''}
                            </span>
                          </div>
                          <div className="reasoning-text">{sug.reasoning}</div>
                          {historyByInvId[String(listing.id)] && (
                            <div className="reasoning-meta">Price path: {historyLine(historyByInvId[String(listing.id)])}</div>
                          )}
                          {sug.verifierNote && (
                            <div className="reasoning-verifier"><span className="reasoning-verifier-label">Reviewer:</span> {sug.verifierNote}</div>
                          )}
                          {sug.flagCodes?.length > 0 && (
                            <div className="reasoning-flags">
                              {sug.flagCodes.map(c => <span key={c} className="ai-flag-chip sev-1" title={FLAG_HELP[c] || c}>{FLAG_LABELS[c] || c}</span>)}
                            </div>
                          )}
                          {/* Deterministic first-price ladder next to the AI's number (initial pricing only) */}
                          {ladderText && <div className="reasoning-meta ai-ladder-line">{ladderText}</div>}
                          {isAdmin && sug.feedback?.mine?.verdict && (
                            <div className="reasoning-meta ai-feedback-line">
                              Your rating: {feedbackLine(sug.feedback.mine)}{sug.feedback.mine.note ? ` — “${sug.feedback.mine.note}”` : ''}
                            </div>
                          )}
                          {isAdmin && !sug.feedback?.mine && sug.feedback?.latestLabel?.verdict && (
                            <div className="reasoning-meta ai-feedback-line">
                              Rated by {sug.feedback.latestLabel.userName || 'an admin'}: {feedbackLine(sug.feedback.latestLabel)}
                            </div>
                          )}
                          <div className="reasoning-actions">
                            {sug.action !== 'hold' && (
                              <button className="btn-primary btn-sm" disabled={savingPrices[listing.id]} onClick={() => handleApplySuggestion(sug, listing)}>
                                Apply {formatCurrency(sug.suggestedPrice)}
                              </button>
                            )}
                            <button className="btn-secondary btn-sm" onClick={() => { handlePriceChange(listing.id, String(sug.suggestedPrice)); setExpandedSuggestionId(null); }}>
                              Edit first
                            </button>
                            <button className="btn-secondary btn-sm" onClick={(e) => handleDismissSuggestion(sug, listing, e.currentTarget)}>Dismiss</button>
                            {isAdmin && (
                              <button
                                className="btn-secondary btn-sm"
                                title="Right / wrong, why, and the price you'd use — never writes to Skybox"
                                onClick={(e) => openRatePopover(sug, listing, e.currentTarget)}
                              >
                                {sug.feedback?.mine ? '★ Edit rating' : '★ Rate'}
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {dismissingSug && (
                    <tr className="dismiss-reason-row">
                      <td colSpan={columnCount}>
                        <DismissReasonBar
                          priceLabel={dismissingSug.action === 'hold' ? 'hold' : shortMoney(dismissingSug.suggestedPrice)}
                          onPick={(code) => confirmDismiss(dismissingSug, code)}
                          onSkip={() => confirmDismiss(dismissingSug, null)}
                          onCancel={() => closeDismissBar(dismissingSug)}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
        </div>
      </div>

      {/* API Debug Panel (admin only) */}
      {isAdminLike(user?.role) && (
      <>
      <button
        className="debug-toggle-btn"
        onClick={() => setShowDebugPanel(!showDebugPanel)}
        title="Toggle API Debug Panel"
      >
        {showDebugPanel ? 'Hide' : 'API'} Debug {apiLogs.length > 0 && `(${apiLogs.length})`}
      </button>

      {showDebugPanel && (
        <div className="debug-panel">
          <div className="debug-panel-header">
            <h3>API Call Log</h3>
            <div className="debug-panel-actions">
              <input
                type="text"
                inputMode="numeric"
                className="debug-lookup-input"
                placeholder="Inventory id"
                value={skyboxLookupId}
                onChange={(e) => setSkyboxLookupId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSkyboxLookup(); }}
                title="Skybox B vs Vivid L vs what-if, plus the POS price trail with who changed it"
              />
              <button className="btn-small" onClick={handleSkyboxLookup} disabled={skyboxLookupBusy || !/^\d+$/.test(skyboxLookupId.trim())}>
                {skyboxLookupBusy ? 'Looking up…' : 'Skybox history'}
              </button>
              <button
                className="btn-small"
                onClick={() => {
                  const logText = apiLogs.map(log => JSON.stringify(log, null, 2)).join('\n\n---\n\n');
                  navigator.clipboard.writeText(logText);
                  setSuccessMessage('Logs copied to clipboard!');
                  setTimeout(() => setSuccessMessage(''), 2000);
                }}
              >
                Copy All
              </button>
              <button className="btn-small" onClick={() => setApiLogs([])}>
                Clear
              </button>
              <button className="btn-small" onClick={() => setShowDebugPanel(false)}>
                Close
              </button>
            </div>
          </div>
          <div className="debug-panel-content">
            {apiLogs.length === 0 ? (
              <div className="debug-empty">
                No API calls logged yet. Try changing a price to see the API calls.
              </div>
            ) : (
              apiLogs.map(log => (
                <div key={log.id} className={`debug-log-entry ${log.type.toLowerCase()}`}>
                  <div className="debug-log-header">
                    <span className={`debug-log-type ${log.type.toLowerCase()}`}>{log.type}</span>
                    <span className="debug-log-method">{log.method}</span>
                    <span className="debug-log-endpoint">{log.endpoint}</span>
                    {log.duration && <span className="debug-log-duration">{log.duration}</span>}
                    {log.status && <span className={`debug-log-status ${log.status === 200 ? 'success' : 'error'}`}>{log.status}</span>}
                  </div>
                  <div className="debug-log-time">{formatTimeEastern(log.timestamp)}</div>
                  {log.inventoryDetails && (
                    <div className="debug-log-details">
                      <strong>Inventory:</strong> {log.inventoryDetails.section} Row {log.inventoryDetails.row} (Qty: {log.inventoryDetails.quantity})
                      <br />
                      <strong>Price Change:</strong> ${log.inventoryDetails.currentBPrice} → ${log.inventoryDetails.newBPrice}
                    </div>
                  )}
                  {log.payload && (
                    <div className="debug-log-payload">
                      <strong>Request Payload:</strong>
                      <pre>{JSON.stringify(log.payload, null, 2)}</pre>
                    </div>
                  )}
                  {log.skyboxApiCall && (
                    <div className="debug-log-skybox">
                      <strong>Skybox API Call:</strong>
                      <pre>{log.skyboxApiCall.endpoint}</pre>
                      <pre>{log.skyboxApiCall.payload}</pre>
                    </div>
                  )}
                  {log.response && (
                    <div className="debug-log-response">
                      <strong>Response:</strong>
                      <pre>{JSON.stringify(log.response, null, 2)}</pre>
                    </div>
                  )}
                  {log.skyboxHistory && (
                    <div className="debug-log-response">
                      <strong>Skybox vs Vivid:</strong>
                      <pre>{[
                        `inventory ${log.skyboxHistory.inventoryId} · ${log.skyboxHistory.section} row ${log.skyboxHistory.row} ×${log.skyboxHistory.quantity} · event ${log.skyboxHistory.eventId ?? '?'}`,
                        `Skybox B $${log.skyboxHistory.listPrice} · Vivid L ${log.skyboxHistory.unitPrice != null ? '$' + log.skyboxHistory.unitPrice : '—'} · what-if for this B ${log.skyboxHistory.whatIf != null ? '$' + log.skyboxHistory.whatIf : '—'}`,
                        `listed ${log.skyboxHistory.listed ?? '?'} · broadcast ${log.skyboxHistory.broadcast} · Vivid listing ${log.skyboxHistory.vividListingId || '—'}`,
                        `last price update ${log.skyboxHistory.lastPriceUpdate ? formatDateTimeEastern(log.skyboxHistory.lastPriceUpdate) : '—'} by ${log.skyboxHistory.lastPriceUpdateBy || '—'}`,
                        skyboxHistoryVerdict(log.skyboxHistory),
                      ].join('\n')}</pre>
                      <strong>Price trail (last {log.skyboxHistory.history?.length || 0} of {log.skyboxHistory.historyCount ?? '?'}; email = Skybox UI, "Prime Lion Tickets" = this tool):</strong>
                      <pre>{(log.skyboxHistory.history || []).map(e => `${formatDateTimeEastern(e.at)}  $${e.price}  ${e.by || '?'}`).join('\n') || '(no price changes recorded)'}</pre>
                    </div>
                  )}
                  {log.error && (
                    <div className="debug-log-error">
                      <strong>Error:</strong> {log.error}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

export default Compare;
